import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BiPlusCircle } from "react-icons/bi";
import { CgRemove } from "react-icons/cg";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { getContestType } from "../../redux/services/contestTypeSlice";
import { getClasses } from "../../redux/services/classesSlice";
import { addContest, editContest, getSingleContest } from "../../redux/services/contestSlice";
import { getCourses } from "../../redux/services/courseSlice";
import { getModules } from "../../redux/services/moduleSlice";
import { getExamType } from "../../redux/services/examTypeSlice";
import moment from "moment-timezone";
import { convertIstToUtc } from "../../common/TimeUtils";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { DatePicker } from "antd";

const ContestForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const getsingleContestData = useSelector((state) => state.contest.singleContestData);
  const queryParams = new URLSearchParams(location.search);
  const contestId = queryParams.get("contestId");
  const getContestTypeData = useSelector((state) => state?.contestType?.contestTypeData);
  const getClassesData = useSelector((state) => state?.classes?.classesData);
  const getCourseData = useSelector((state) => state?.course?.courseData);
  const getModulesData = useSelector((state) => state?.module?.moduleData);
  const getExamTypeData = useSelector((state) => state?.examType?.examTypeData);

  const coursesList = getCourseData?.course_data || (Array.isArray(getCourseData) ? getCourseData : []);
  const modulesList = getModulesData?.module_data || (Array.isArray(getModulesData) ? getModulesData : []);
  const examTypesList = getExamTypeData?.exam_type_data || (Array.isArray(getExamTypeData) ? getExamTypeData : []);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState();
  const initialValues = {
    name: undefined,
    contestTypeId: undefined,
    startDate: undefined,
    endDate: undefined,
    totalSpots: undefined,
    fees: undefined,
    classesFees: undefined,
    winningAmountPerFee: undefined,
    winnerPercentage: undefined,
    ranks: [
      {
        startPlace: undefined,
        price: undefined,
      },
    ],
    totalQuestions: undefined,
    totalTime: undefined,
    totalMarks: undefined,
    classesIds: [],
    courseId: undefined,
    moduleId: undefined,
    examTypeId: undefined,
    isOfflineOMRAvailable: false,
    isLifetime: false,
  };
  const validationSchema = Yup.object({
    name: Yup.string().required("Contest name is required").min(3, "Contest name must be at least 3 characters"),
    contestTypeId: Yup.string().required("Contest type is required"),
    startDate: Yup.date().nullable().when("isLifetime", {
      is: true,
      then: () => Yup.date().nullable().optional(),
      otherwise: () => Yup.date().required("Start date is required").nullable()
    }),
    endDate: Yup.date().nullable().when("isLifetime", {
      is: true,
      then: () => Yup.date().nullable().optional(),
      otherwise: () => Yup.date().required("End date is required").nullable().min(Yup.ref("startDate"), "End date must be after the start date")
    }),
    totalSpots: Yup.number().when("isLifetime", {
      is: true,
      then: () => Yup.number().optional(),
      otherwise: () => Yup.number().required("Total spots are required").min(1, "Total spots must be at least 1")
    }),
    fees: Yup.number().when("isLifetime", {
      is: true,
      then: () => Yup.number().optional(),
      otherwise: () => Yup.number().required("Fees are required").min(0, "Fees cannot be negative")
    }),
    classesFees: Yup.number().when("isLifetime", {
      is: true,
      then: () => Yup.number().optional(),
      otherwise: () => Yup.number().required("Classes fees are required").min(0, "Classes fees cannot be negative")
    }),
    winningAmountPerFee: Yup.number().when("isLifetime", {
      is: true,
      then: () => Yup.number().optional(),
      otherwise: () => Yup.number().required("Winning amount per fee is required").min(0, "Winning amount cannot be negative")
    }),
    winnerPercentage: Yup.number().when("isLifetime", {
      is: true,
      then: () => Yup.number().optional(),
      otherwise: () => Yup.number().required("Winner percentage is required").min(0, "Winner percentage cannot be negative").max(100, "Winner percentage cannot exceed 100")
    }),
    totalQuestions: Yup.number().required("Total questions are required").min(1, "Total questions must be at least 1"),
    totalMarks: Yup.number().required("Total marks are required").min(1, "Total marks must be at least 1"),
    classesIds: Yup.array().of(Yup.string().required("Invalid class selected")).min(1, "At least one class must be selected").required("Classes are required"),
    courseId: Yup.string().optional().nullable(),
    moduleId: Yup.string().optional().nullable(),
    examTypeId: Yup.string().optional().nullable(),
    isOfflineOMRAvailable: Yup.boolean().optional(),
    isLifetime: Yup.boolean().optional(),
  });

  const onSubmit = async () => {
    if (values.isLifetime) {
      values.startDate = moment().format("YYYY-MM-DDTHH:mm");
      values.endDate = moment().add(100, "years").format("YYYY-MM-DDTHH:mm");
      values.totalSpots = 9999999;
      values.fees = 0;
      values.classesFees = 0;
      values.winningAmountPerFee = 0;
      values.winnerPercentage = 0;
      values.pricePool = 0;
    } else {
      const totalRankPrice = ranks.reduce((total, rank) => total + (rank?.price || 0), 0);

      if (totalRankPrice > values.pricePool) {
        setMessage("Total rank prices must be less than or equal to the price pool.");
        return;
      }
      const isPriceOrderValid = checkPriceOrder();
      if (!isPriceOrderValid) {
        return;
      }
    }
    const SDate = values.startDate ? convertIstToUtc(values.startDate) : null;
    const EDate = values.endDate ? convertIstToUtc(values.endDate) : null;
    const filteredRanks = values.isLifetime ? [] : ranks.filter((rank) => rank.startPlace && rank.price);
    const updatedValues = {
      ...values,
      startDate: SDate,
      endDate: EDate,
      ranks: filteredRanks.map((rank) => ({
        startPlace: rank?.startPlace,
        price: rank?.price,
      })),
    };
    const payload = { ...updatedValues };
    if (contestId) {
      payload.contestId = contestId;
    }
    try {
      let response;
      if (contestId) {
        response = await dispatch(editContest(payload));
      } else {
        response = await dispatch(addContest(updatedValues));
      }
      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        setLoading(false);
        navigate("/contest");
        resetForm();
      } else {
      }
    } catch (error) { }
  };
  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setValues, resetForm } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });
  const [ranks, setRanks] = useState(initialValues.ranks);
  useEffect(() => {
    setFieldValue("ranks", ranks);
  }, [ranks, setFieldValue]);
  const removeRank = (index) => {
    const updatedRanks = [...ranks];
    updatedRanks.splice(index, 1);
    setRanks(updatedRanks);
  };

  const calculateTimeDifference = (start, end) => {
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffInMilliseconds = endDate - startDate;
      const diffInMinutes = Math.floor(diffInMilliseconds / 60000);
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      return { hours, minutes };
    }
    return { hours: 0, minutes: 0 };
  };
  const [rankErrors, setRankErrors] = useState([]);
  const handleRankChange = (index, field, value) => {
    const updatedRanks = [...ranks];
    const updatedRank = { ...updatedRanks[index] };
    updatedRank[field] = field === "price" && value !== "" ? parseFloat(value) : value;
    updatedRanks[index] = updatedRank;
    setRanks(updatedRanks);
    setFieldValue("ranks", updatedRanks);

    checkPriceOrder();
  };
  const addRank = () => {
    const totalRankPrice = ranks.reduce((total, rank) => total + (rank?.price || 0), 0);

    const newRankPrice = 0;
    const newTotalRankPrice = totalRankPrice + newRankPrice;

    if (newTotalRankPrice > values.pricePool) {
      setMessage("Total rank prices must be less than or equal to the price pool.");
      return;
    } else {
      setMessage("");
    }

    const updatedRanks = [...ranks];
    const lastRank = updatedRanks[updatedRanks.length - 1];
    const newStartPlace = lastRank && typeof lastRank.rank === "number" ? lastRank.rank + 1 : "";
    const newRank = {
      startPlace: newStartPlace,
      price: "",
    };
    updatedRanks.push(newRank);
    setRanks(updatedRanks);
    setFieldValue("ranks", updatedRanks);
  };
  const checkPriceOrder = useCallback(() => {
    const errors = [];
    let isValid = true;

    for (let i = 1; i < ranks.length; i++) {
      const currentPrice = ranks[i]?.price;
      const previousPrice = ranks[i - 1]?.price;

      if (typeof currentPrice === "number" && typeof previousPrice === "number") {
        if (currentPrice >= previousPrice) {
          errors[i] = "Price must be less than the previous rank.";
          isValid = false;
        } else {
          errors[i] = "";
        }
      }
    }
    setRankErrors(errors);
    return isValid;
  }, [ranks]);

  useEffect(() => {
    if (ranks) {
      checkPriceOrder();
    }
  }, [ranks, checkPriceOrder]);

  const isPriceOrderValid = useMemo(() => checkPriceOrder(), [checkPriceOrder]);

  useEffect(() => {
    if (values.startDate && values.endDate) {
      const { hours, minutes } = calculateTimeDifference(values.startDate, values.endDate);
      setFieldValue("totalTime", `${hours} hours ${minutes} minutes`);
    }
  }, [values.startDate, values.endDate, setFieldValue]);
  const getAllContest = useCallback(async () => {
    await dispatch(getContestType({ page: 1, limit: 100 }));
  }, [dispatch]);

  const getAllClasses = useCallback(async () => {
    await dispatch(getClasses({ page: 1, limit: 100 }));
  }, [dispatch]);

  const getAllCourses = useCallback(async () => {
    await dispatch(getCourses({ page: 1, limit: 100 }));
  }, [dispatch]);

  const getAllExamTypes = useCallback(async () => {
    await dispatch(getExamType({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    getAllContest();
    getAllClasses();
    getAllCourses();
    getAllExamTypes();
  }, [getAllContest, getAllClasses, getAllCourses, getAllExamTypes]);

  useEffect(() => {
    if (values.courseId) {
      dispatch(getModules({ page: 1, limit: 100, courseId: values.courseId }));
    }
  }, [values.courseId, dispatch]);

  const handleStartDateChange = (_, dateString) => {
    const selectedStartDate = dateString;
    handleChange({
      target: {
        name: "startDate",
        value: selectedStartDate,
      },
    });
    if (!selectedStartDate) {
      setFieldValue("endDate", "");
    }
  };
  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleContest(contestId));
    } catch (error) { }
  }, [dispatch, contestId]);

  useEffect(() => {
    if (contestId) {
      getSingleRecord();
    }
  }, [contestId, getSingleRecord]);
  useEffect(() => {
    if (contestId && getsingleContestData) {
      const startDateInIST = moment(getsingleContestData?.startDate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm");
      const endDateInIST = moment(getsingleContestData?.endDate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm");
      setValues({
        name: getsingleContestData?.name ?? undefined,
        contestTypeId: getsingleContestData?.contestTypeId ?? undefined,
        startDate: startDateInIST ?? undefined,
        endDate: endDateInIST ?? undefined,
        totalSpots: getsingleContestData?.totalSpots ?? undefined,
        fees: getsingleContestData?.fees ?? undefined,
        classesFees: getsingleContestData?.classesFees ?? undefined,
        winningAmountPerFee: getsingleContestData?.winningAmountPerFee ?? undefined,
        winnerPercentage: getsingleContestData?.winnerPercentage ?? undefined,
        totalQuestions: getsingleContestData?.totalQuestions ?? undefined,
        totalMarks: getsingleContestData?.totalMarks ?? undefined,
        totalTime: getsingleContestData?.totalTime ?? undefined,
        classesIds: getsingleContestData?.classesIds ?? undefined,
        courseId: getsingleContestData?.courseId ?? undefined,
        moduleId: getsingleContestData?.moduleId ?? undefined,
        examTypeId: getsingleContestData?.examTypeId ?? undefined,
        isOfflineOMRAvailable: getsingleContestData?.isOfflineOMRAvailable ?? false,
        isLifetime: getsingleContestData?.isLifetime ?? false,
      });
      setRanks(getsingleContestData?.ranks.length === 0 ? initialValues?.ranks : getsingleContestData?.ranks);
    }
  }, [contestId, getsingleContestData, setValues]);
  const classOptions =
    getClassesData?.classes_data?.map((contestType) => ({
      value: contestType._id,
      label: contestType.name,
    })) || [];

  const contestTypeOptions =
    getContestTypeData?.contest_type_data?.map((contestType) => ({
      value: contestType._id,
      label: contestType.name,
    })) || [];

  const courseOptions =
    coursesList?.map((course) => ({
      value: course._id,
      label: course.title,
    })) || [];

  const moduleOptions =
    modulesList?.map((mod) => ({
      value: mod._id,
      label: mod.name,
    })) || [];

  const examTypeOptions =
    examTypesList?.map((et) => ({
      value: et._id,
      label: et.name,
    })) || [];

  useEffect(() => {
    const calculatedPricePool = values.totalSpots * values.winningAmountPerFee;
    setFieldValue("pricePool", calculatedPricePool);
  }, [values.totalSpots, values.winningAmountPerFee, setFieldValue]);

  // const FilterClasses = getClassesData?.classes_data?.filter((option) => values.classesIds.includes(option._id));


  // useEffect(() => {
  //   if (values.totalQuestions && FilterClasses?.length > 0) {
  //     const positiveMarks = Number(FilterClasses[0]?.positiveMarks ?? 0);
  //     const calculatedMarks = (((values.totalQuestions * 11) / 10) * positiveMarks);
  //     // const roundedMarks =
  //     //   calculatedMarks % 1 < 0.5
  //     //     ? Math.floor(calculatedMarks)
  //     //     : Math.ceil(calculatedMarks);
  //     setFieldValue("totalMarks", calculatedMarks);
  //   }
  // }, [values.totalQuestions, setFieldValue, FilterClasses]);

  useEffect(() => {
    if (values.totalQuestions) {
      const FilterClasses = getClassesData?.classes_data?.filter((option) => values.classesIds.includes(option._id));
      if (FilterClasses?.length > 0) {
        const positiveMarks = Number(FilterClasses[0]?.positiveMarks ?? 0);
        const calculatedMarks = (((values.totalQuestions * 11) / 10) * positiveMarks);
        setFieldValue("totalMarks", calculatedMarks);
      }
    }
  }, [values.totalQuestions, values.classesIds, getClassesData?.classes_data, setFieldValue]);

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">{action} Contest</p>
          <p className="md:text-lg text-center font-normal text-muted-foreground">Manage your contests easily from here.</p>
          <form className="mt-6" onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-5">
              <div>
                <label className="text-sm font-medium text-foreground">Classes Name</label>
                <Select
                  isMulti
                  name="classesIds"
                  options={classOptions}
                  className="basic-multi-select "
                  classNamePrefix="select"
                  value={classOptions.filter((option) => values.classesIds.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                    setFieldValue("classesIds", selectedValues);
                  }}
                  onBlur={handleBlur}
                />
                {touched.classesIds && errors.classesIds && <div className="text-red-500 text-sm">{errors.classesIds}</div>}
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Contest Name</label>
                <input type="text" name="name" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Contest Name" />
                {touched.name && errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Contest Type</label>
                <Select
                  name="contestTypeId"
                  options={contestTypeOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  value={contestTypeOptions.find((option) => option.value === values.contestTypeId) || null}
                  onChange={(selectedOption) => {
                    setFieldValue("contestTypeId", selectedOption ? selectedOption.value : "");
                  }}
                  onBlur={handleBlur}
                  placeholder="Select Contest Type"
                  isClearable
                />
                {touched.contestTypeId && errors.contestTypeId && <div className="text-red-500 text-sm">{errors.contestTypeId}</div>}
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Classes Fees</label>
                <input type="number" name="classesFees" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={values.classesFees} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Classes Fees" />
                {touched.classesFees && errors.classesFees && <div className="text-red-500 text-sm">{errors.classesFees}</div>}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Exam Type (Optional)</label>
                <Select
                  name="examTypeId"
                  options={examTypeOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  value={examTypeOptions.find((option) => option.value === values.examTypeId) || null}
                  onChange={(selectedOption) => {
                    setFieldValue("examTypeId", selectedOption ? selectedOption.value : undefined);
                    if (!selectedOption) {
                      setFieldValue("courseId", undefined);
                      setFieldValue("moduleId", undefined);
                    }
                  }}
                  onBlur={handleBlur}
                  placeholder="Select Exam Type"
                  isClearable
                />
                {touched.examTypeId && errors.examTypeId && <div className="text-red-500 text-sm">{errors.examTypeId}</div>}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Course (Optional)</label>
                <Select
                  name="courseId"
                  options={courseOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  isDisabled={!values.examTypeId}
                  value={courseOptions.find((option) => option.value === values.courseId) || null}
                  onChange={(selectedOption) => {
                    const selectedCourseId = selectedOption ? selectedOption.value : undefined;
                    setFieldValue("courseId", selectedCourseId);
                    setFieldValue("moduleId", undefined); // Reset module when course changes
                  }}
                  onBlur={handleBlur}
                  placeholder="Select Course"
                  isClearable
                />
                {touched.courseId && errors.courseId && <div className="text-red-500 text-sm">{errors.courseId}</div>}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Module (Optional)</label>
                <Select
                  name="moduleId"
                  options={moduleOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  isDisabled={!values.courseId}
                  value={moduleOptions.find((option) => option.value === values.moduleId) || null}
                  onChange={(selectedOption) => {
                    setFieldValue("moduleId", selectedOption ? selectedOption.value : undefined);
                  }}
                  onBlur={handleBlur}
                  placeholder="Select Module"
                  isClearable
                />
                {touched.moduleId && errors.moduleId && <div className="text-red-500 text-sm">{errors.moduleId}</div>}
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  name="isOfflineOMRAvailable"
                  id="isOfflineOMRAvailable"
                  checked={values.isOfflineOMRAvailable || false}
                  onChange={(e) => setFieldValue("isOfflineOMRAvailable", e.target.checked)}
                  className="h-5 w-5 rounded border-border text-primary"
                />
                <label htmlFor="isOfflineOMRAvailable" className="text-sm font-semibold text-foreground cursor-pointer">
                  Offline OMR Scanner Available
                </label>
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  name="isLifetime"
                  id="isLifetime"
                  checked={values.isLifetime || false}
                  onChange={(e) => setFieldValue("isLifetime", e.target.checked)}
                  className="h-5 w-5 rounded border-border text-primary"
                />
                <label htmlFor="isLifetime" className="text-sm font-semibold text-foreground cursor-pointer">
                  Lifetime Exam (Ongoing, Unlimited attempts, Free entry)
                </label>
              </div>

              {!values.isLifetime && (
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-foreground mt-1">Start Date</label>
                  <div className="w-full">
                    <DatePicker
                      showTime
                      name="startDate"
                      value={values.startDate ? moment(values?.startDate) : null}
                      onChange={handleStartDateChange}
                      onBlur={handleBlur}
                      disabled={!!contestId}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      className={`${!!contestId && "bg-background outline-none"} w-full px-4 py-2 border border-border rounded-xl`}
                      format="YYYY-MM-DD HH:mm"
                      disabledDate={(current) => current && current < moment().startOf("day")}
                      disabledTime={(current) => {
                        if (current && current.isSame(moment(), "day")) {
                          return {
                            disabledHours: () => [...Array(24).keys()].filter((h) => h < moment().hour()),
                            disabledMinutes: (selectedHour) => {
                              if (selectedHour === moment().hour()) {
                                return [...Array(60).keys()].filter((m) => m < moment().minute());
                              }
                              return [];
                            },
                            disabledSeconds: (selectedHour, selectedMinute) => {
                              if (selectedHour === moment().hour() && selectedMinute === moment().minute()) {
                                return [...Array(60).keys()].filter((s) => s < moment().second());
                              }
                              return [];
                            },
                          };
                        }
                        return {};
                      }}
                    />
                  </div>
                  {touched.startDate && errors.startDate && <div className="text-red-500 text-sm">{errors.startDate}</div>}
                </div>
              )}

              {!values.isLifetime && (
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-foreground mt-1">End Date</label>
                  <DatePicker
                    showTime
                    name="endDate"
                    disabled={!values.startDate}
                    value={values.endDate ? moment(values.endDate) : null}
                    onChange={(date, dateString) => {
                      if (date) {
                        handleChange({
                          target: {
                            name: "endDate",
                            value: dateString,
                          },
                        });
                      }
                    }}
                    onBlur={handleBlur}
                    className="w-full px-4 py-2 border border-border rounded-xl"
                    format="YYYY-MM-DD HH:mm"
                    getPopupContainer={(trigger) => trigger.parentNode}
                    disabledDate={(current) => current && current < moment(values.startDate).startOf("day")}
                    disabledTime={(current) => {
                      if (moment(values.startDate).isSame(current, "day")) {
                        return {
                          disabledHours: () => [...Array(24).keys()].filter((h) => h < moment(values.startDate).hour()),
                          disabledMinutes: (selectedHour) => {
                            if (selectedHour === moment(values.startDate).hour()) {
                              return [...Array(60).keys()].filter((m) => m < moment(values.startDate).minute());
                            }
                            return [];
                          },
                          disabledSeconds: (selectedHour, selectedMinute) => {
                            if (selectedHour === moment(values.startDate).hour() && selectedMinute === moment(values.startDate).minute()) {
                              return [...Array(60).keys()].filter((s) => s < moment(values.startDate).second());
                            }
                            return [];
                          },
                        };
                      }
                      return {};
                    }}
                  />
                  {touched.endDate && errors.endDate && <div className="text-red-500 text-sm">{errors.endDate}</div>}
                </div>
              )}

              {!values.isLifetime && (
                <div>
                  <label className="text-sm font-medium text-foreground">Total Time</label>
                  <input type="text" name="totalTime" className="w-full px-4 py-2 border border-border rounded-xl bg-background outline-none" value={values.totalTime} onChange={handleChange} onBlur={handleBlur} placeholder="Total Time" readOnly />
                  {touched.totalTime && errors.totalTime && <div className="text-red-500 text-sm">{errors.totalTime}</div>}
                </div>
              )}
            </div>
            <div>
              <p className="text-xl mt-7 font-medium uppercase text-primary">
                {!values.isLifetime ? "Participation And Rewards" : "Question & Marks Details"}
              </p>
              <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-5 mt-3">
                {!values.isLifetime && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Total Spots</label>
                    <input type="number" name="totalSpots" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={values.totalSpots} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Total Spots" />
                    {touched.totalSpots && errors.totalSpots && <div className="text-red-500 text-sm">{errors.totalSpots}</div>}
                  </div>
                )}
                {!values.isLifetime && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Winning Amount Per Fee</label>
                    <input type="number" name="winningAmountPerFee" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={values.winningAmountPerFee} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Winning Amount" />
                    {touched.winningAmountPerFee && errors.winningAmountPerFee && <div className="text-red-500 text-sm">{errors.winningAmountPerFee}</div>}
                  </div>
                )}
                {!values.isLifetime && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Price Pool</label>
                    <input type="number" name="pricePool" className="w-full px-4 py-2 border border-border rounded-xl bg-background outline-none no-arrows" placeholder="Price Pool" value={values?.pricePool} readOnly />
                  </div>
                )}
                {!values.isLifetime && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Exam Fee</label>
                    <input type="number" name="fees" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={values.fees} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Exam Fee" />
                    {touched.fees && errors.fees && <div className="text-red-500 text-sm">{errors.fees}</div>}
                  </div>
                )}
                {!values.isLifetime && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Winner Percentage</label>
                    <input type="number" name="winnerPercentage" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={values.winnerPercentage} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Winner Percentage" />
                    {touched.winnerPercentage && errors.winnerPercentage && <div className="text-red-500 text-sm">{errors.winnerPercentage}</div>}
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-foreground">Total Questions</label>
                  <input type="number" name="totalQuestions" className="w-full px-4 py-2 border border-border rounded-xl no-arrows" disabled={values.classesIds.length === 0} value={values.totalQuestions} onChange={handleChange} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Total Questions" />
                  {touched.totalQuestions && errors.totalQuestions && <div className="text-red-500 text-sm">{errors.totalQuestions}</div>}
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Total Marks</label>
                  <input type="number" name="totalMarks" className="w-full px-4 py-2 border border-border rounded-xl bg-background outline-none no-arrows" placeholder="Total Marks" value={values.totalMarks} readOnly />
                  {touched.totalMarks && errors.totalMarks && <div className="text-red-500 text-sm">{errors.totalMarks}</div>}
                </div>
              </div>
            </div>
            {!values.isLifetime && (
              <div>
                <p className="text-xl mt-7 font-medium uppercase text-primary">Rank Section</p>
                <div className="mt-3">
                  {ranks.map((rank, index) => (
                    <div key={index} className="flex md:gap-3 gap-1">
                      <div className="flex gap-2 md:w-auto w-[calc(100vw-5rem)]">
                        <div>
                          <label className="text-sm font-medium text-foreground">Rank</label>
                          <input type="text" name={`ranks[${index}].startPlace`} className="w-full px-4 py-2 border border-border rounded-xl" value={rank.startPlace} onChange={(e) => handleRankChange(index, "startPlace", e.target.value)} onBlur={handleBlur} placeholder="Start Place" />
                          {touched.ranks?.[index]?.startPlace && errors.ranks?.[index]?.startPlace && <div className="text-red-500 text-sm">{errors.ranks[index].startPlace}</div>}
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground">Price</label>
                          <input type="number" name={`ranks[${index}].price`} className="w-full px-4 py-2 border border-border rounded-xl no-arrows" value={rank?.price} onChange={(e) => handleRankChange(index, "price", e.target.value)} onBlur={handleBlur} onWheel={(e) => e.target.blur()} onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()} placeholder="Price" />
                          {touched.ranks?.[index]?.price && <div className="text-red-500 text-sm">{errors.ranks?.[index]?.price}</div>}
                          {rankErrors[index] && <div className="text-red-500 text-sm">{rankErrors[index]}</div>}
                        </div>
                      </div>
                      {ranks.length > 1 && (
                        <button type="button" onClick={() => removeRank(index)} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-red-500 rounded-xl hover:bg-red-600">
                          <CgRemove className="text-sm sm:text-xl" />
                        </button>
                      )}

                      {index === ranks.length - 1 && (
                        <button type="button" onClick={addRank} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-primary rounded-xl" disabled={!isPriceOrderValid}>
                          <BiPlusCircle className="text-sm sm:text-2xl" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {message && <div className="text-red-500 text-sm">{message}</div>}
              </div>
            )}

            <div className="flex justify-center">
              <button type="submit" className="w-full sm:w-[200px] min-h-[50px] flex justify-center items-center text-white bg-primary rounded-xl hover:bg-accent mt-7">
                {loading ? <div className="loader"></div> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContestForm;
